import {
  IFleetData,
  IFleetShip,
  IFleetShipSelection,
  IShipUpgrade,
  IShipWeapon,
  ShipWeaponFacing
} from "@/data/types";
import FtlService from "@/services/list-builder/FtlService";
import StudioApiService from "@/services/StudioApiService";
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { nanoid } from "nanoid";

export interface IFtlState {
  fleets: IFleetListItem[];
  selectedFleets: IFleetData[];
  ships: IFleetShipSelection[];
  selectedShipId: string | null;
  shipPreview: IFleetShipSelection | null;
  upgradeLibrary: { name: string; rule: string; fleetId: string }[];
  upgradesOpen: ShipUpgradeSectionType | null;
}

export enum ShipUpgradeSectionType {
  hero = 1,
  title = 2,
  systems = 3,
  weapons = 4
}

export interface IFleetListItem {
  uid: string;
  name: string;
  coverImagePath: string;
}

const initialState: IFtlState = {
  fleets: [],
  selectedFleets: [],
  ships: [],
  selectedShipId: null,
  shipPreview: null,
  upgradeLibrary: [],
  upgradesOpen: null
};

export const loadFleets = createAsyncThunk("ftl/loadFleets", async () => {
  return await StudioApiService.getFleets();
});

export const loadFleet = createAsyncThunk("ftl/loadFleet", async (uid: string, { getState }) => {
  const state = (getState() as any).ftl as IFtlState;
  const commonFleetExisting = state.selectedFleets.find((x) => x.uid === "common");

  const requestedFleet = await StudioApiService.getFleet(uid);

  if (commonFleetExisting) return [requestedFleet, commonFleetExisting];

  const commonFleet = await StudioApiService.getFleet("common");
  return [requestedFleet, commonFleet];
});

const ftlSlice = createSlice({
  name: "ftl",
  initialState: initialState,
  reducers: {
    resetLoadedFleets(state) {
      state.selectedFleets = [];
    },
    loadSavedShips(state, action: PayloadAction<IFleetShipSelection[]>) {
      state.ships = action.payload;
    },
    previewShip(state, action: PayloadAction<IFleetShip>) {
      state.shipPreview = {
        ...action.payload,
        selectionId: "",
        systems: [],
        weapons: [],
        notes: "",
        customName: "",
        count: 1,
        xp: 0,
        traits: []
      };
      state.selectedShipId = null;
    },
    addShip(state, action: PayloadAction<IFleetShip>) {
      const ship = action.payload;
      const systems = [];

      // Warp Gate
      if (ship.special.includes("Warp Gate")) {
        const ss = state.selectedFleets
          .flatMap((x) => x.upgrades)
          .find((x) => x.name === "Spatial Superposition");
        systems.push(ss);
        systems.push(ss);
        systems.push(ss);
        systems.push(ss);
      }

      const addShip = () => {
        state.ships.push({
          ...action.payload,
          selectionId: nanoid(8),
          customName: "",
          notes: "",
          systems: systems,
          weapons: [],
          count: 1,
          xp: 0,
          traits: []
        });
      };
      const existing = ship.squadron && state.ships.find((x) => x.id === ship.id);

      // If it's a squadron, increment the count if possible
      if (ship.squadron && existing) {
        existing.count++;
      } else {
        addShip();
      }
    },
    duplicateShip(state, action: PayloadAction<IFleetShipSelection>) {
      const newShip: IFleetShipSelection = { ...action.payload, selectionId: nanoid(8) };

      state.ships.push(newShip);
    },
    selectShip(state, action: PayloadAction<string>) {
      const shipSelectionId = action.payload;
      state.selectedShipId = shipSelectionId;
      state.shipPreview = null;
    },
    adjustShipXp(state, action: PayloadAction<{ shipId: string; xp: number }>) {
      const { shipId, xp } = action.payload;
      const ship = state.ships.filter((u) => u.selectionId === shipId)[0];
      ship.xp += xp;
    },
    removeShip(state, action: PayloadAction<string>) {
      const removeIndex = state.ships.findIndex((x) => x.selectionId === action.payload);
      if (state.ships[removeIndex].count > 1) {
        state.ships[removeIndex].count--;
      } else {
        state.ships.splice(removeIndex, 1);
      }
    },
    renameShip(state, action: PayloadAction<{ unitId: string; name: string }>) {
      const { unitId, name } = action.payload;
      const ship = state.ships.filter((u) => u.selectionId === unitId)[0];
      ship.customName = name;
    },
    setShipNotes(state, action: PayloadAction<{ unitId: string; notes: string }>) {
      const { unitId, notes } = action.payload;
      const ship = state.ships.find((u) => u.selectionId === unitId);
      ship.notes = notes;
    },
    setShipHero(state, action: PayloadAction<{ unitId: string; value: string | null }>) {
      const { unitId, value } = action.payload;
      const ship = state.ships.find((u) => u.selectionId === unitId);
      const hero = state.selectedFleets.flatMap((x) => x.heroes).find((x) => x.id === value);
      if (value) {
        ship.hero = hero;
      } else {
        delete ship.hero;
      }
    },
    setShipTitle(state, action: PayloadAction<{ unitId: string; value: string | null }>) {
      const { unitId, value } = action.payload;
      const ship = state.ships.find((u) => u.selectionId === unitId);
      const title = state.selectedFleets.flatMap((x) => x.titles).find((x) => x.id === value);
      if (value) {
        ship.title = title;
      } else {
        delete ship.title;
      }
    },
    setShipSystem(state, action: PayloadAction<{ unitId: string; value: IShipUpgrade }>) {
      const { unitId, value } = action.payload;
      const ship = state.ships.find((u) => u.selectionId === unitId);
      ship.systems.push(value);
    },
    removeSystem(state, action: PayloadAction<{ unitId: string; value: IShipUpgrade }>) {
      const { unitId, value } = action.payload;
      const ship = state.ships.find((u) => u.selectionId === unitId);
      const index = ship.systems.findIndex((x) => x.id === value.id);
      ship.systems.splice(index, 1);
    },
    setShipWeapon(state, action: PayloadAction<{ unitId: string; value: IShipWeapon }>) {
      const { unitId, value } = action.payload;
      const ship = state.ships.find((u) => u.selectionId === unitId);
      const strikeVessel = ship.special.includes("Strike Vessel");
      const arsenalVessel = ship.special.includes("Arsenal Vessel");
      const facing: ShipWeaponFacing = value.facing;
      const allowedInSlot = (() => {
        if (facing === ShipWeaponFacing.front) {
          if (strikeVessel) return 2;
          if (arsenalVessel) return 3;
        }
        if (facing === ShipWeaponFacing.sides) {
          if (arsenalVessel) return 2;
        }
        return 1;
      })();

      // remove any weapons of this facing
      const countInSlot = ship.weapons.filter((x) => x.facing === value.facing).length;
      const existingInSlot = ship.weapons.findIndex((x) => x.facing === value.facing);
      const maxInSlot = countInSlot >= allowedInSlot;
      const shipFull = FtlService.getOccupiedSlotCount(ship) >= ship.slots;

      if ((maxInSlot || shipFull) && existingInSlot >= 0) {
        ship.weapons.splice(existingInSlot, 1);
      }

      // if this weapon is selected in another facing, remove it
      const existingWeapon = ship.weapons.findIndex((x) => x.id === value.id);
      if (existingWeapon >= 0) {
        ship.weapons.splice(existingWeapon, 1);
      }

      ship.weapons.push(value);
    },
    removeWeapon(state, action: PayloadAction<{ unitId: string; value: IShipWeapon }>) {
      const { unitId, value } = action.payload;
      const ship = state.ships.find((u) => u.selectionId === unitId);
      // remove any weapons of this facing
      const existingInSlot = ship.weapons.findIndex(
        (x) => value.name === x.name && x.facing === value.facing
      );
      ship.weapons.splice(existingInSlot, 1);
    },
    setUpgradesOpen(state, action: PayloadAction<ShipUpgradeSectionType | null>) {
      state.upgradesOpen = action.payload;
    },
    resetShipUpgrades(
      state,
      action: PayloadAction<{
        unitId: string;
        type: ShipUpgradeSectionType | null;
      }>
    ) {
      const { unitId, type } = action.payload;
      const ship = state.ships.find((u) => u.selectionId === unitId);

      switch (type) {
        case ShipUpgradeSectionType.hero:
          ship.hero = null;
          break;
        case ShipUpgradeSectionType.title:
          ship.title = null;
          break;
        case ShipUpgradeSectionType.systems:
          ship.systems = [];
          break;
        case ShipUpgradeSectionType.weapons:
          ship.weapons = [];
          break;
      }
    },
    unloadFleet(state, action: PayloadAction<string>) {
      state.selectedFleets = state.selectedFleets.filter((x) => x.uid !== action.payload);
    },
    removeShipsForFleet(state, action: PayloadAction<string>) {
      state.ships = state.ships.filter((x) => x.fleetId !== action.payload);
    },
    toggleShipTrait(state, action: PayloadAction<{ shipId: string; trait: string }>) {
      const { shipId, trait } = action.payload;
      const ship = state.ships.find((u) => u.selectionId === shipId);
      const existingTraitIndex = ship.traits.findIndex((t) => t === trait);
      if (existingTraitIndex >= 0) {
        ship.traits.splice(existingTraitIndex, 1);
      } else {
        ship.traits.push(trait);
      }
    },
    adjustUpgradeDamage(
      state,
      action: PayloadAction<{ shipId: string; weaponId?: string; systemId?: string; amt: number }>
    ) {
      const { shipId, weaponId, systemId, amt } = action.payload;
      const ship = state.ships.find((u) => u.selectionId === shipId);
      const upgrade = weaponId
        ? ship.weapons.find((x) => x.id === weaponId)
        : ship.systems.find((x) => x.id === systemId);
      if (!upgrade.damage) upgrade.damage = 0;
      upgrade.damage += amt;
    }
  },
  selectors: {
    selectFleetPoints: (state) => FtlService.calculateListTotal(state.ships)
  },
  extraReducers(builder) {
    builder.addCase(loadFleets.fulfilled, (state, action) => {
      return { ...state, fleets: action.payload };
    });
    builder.addCase(loadFleet.fulfilled, (state, action: PayloadAction<IFleetData[]>) => {
      const commonIsLoaded = state.selectedFleets.some((x) => x.uid === "common");
      const fleets = commonIsLoaded ? action.payload.filter((x) => x.uid !== "common") : action.payload;
      const upgrades: any = fleets
        .flatMap((x) => x.upgrades)
        .concat(fleets.flatMap((x) => x.heroes))
        .concat(fleets.flatMap((x) => x.titles))
        .concat(fleets.flatMap((x) => x.specialRules));
      const allFleets = state.selectedFleets
        .filter((x) => !fleets.some((fleet) => fleet.uid === x.uid)) // Remove them if already loaded
        .concat(...fleets);
      const common = allFleets.find((x) => x.uid === "common");

      return {
        ...state,
        selectedFleets: [...allFleets.filter((x) => x.uid !== "common"), common],
        upgradeLibrary: state.upgradeLibrary.concat(...upgrades)
      };
    });
  }
});

export const { selectFleetPoints } = ftlSlice.selectors;

// Action creators are generated for each case reducer function
export const {
  loadSavedShips,
  addShip,
  selectShip,
  removeShip,
  setShipNotes,
  renameShip,
  previewShip,
  setShipTitle,
  setShipHero,
  setShipSystem,
  removeSystem,
  setShipWeapon,
  removeWeapon,
  setUpgradesOpen,
  resetShipUpgrades,
  duplicateShip,
  adjustShipXp,
  unloadFleet,
  removeShipsForFleet,
  toggleShipTrait,
  adjustUpgradeDamage,
  resetLoadedFleets
} = ftlSlice.actions;

export default ftlSlice.reducer;
